import React, {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTSVG} from '../../../_metronic/helpers'

const API_URL = process.env.REACT_APP_API_URL

type FilterOption = {
  id: number
  name?: string | null
  isActive?: boolean
}

type DepartmentExamStatusSummary = {
  totalCourseCount: number
  createdExamCount: number
  missingExamCount: number
  submittedExamCount: number
  plannedExamCount: number
}

type DepartmentExamStatusRow = {
  semesterCourseId: number
  semesterName?: string | null
  departmentName?: string | null
  courseCode?: string | null
  courseName?: string | null
  lecturerName?: string | null
  studentCount?: number | null
  examTypeName?: string | null
  examId?: number | null
  status?: string | null
  statusLabel?: string | null
  statusOrder: number
  isExamCreated: boolean
  isSubmittedToMeasurementCenter: boolean
  isPlanned: boolean
  scheduleCount: number
  scheduleSummary?: string | null
  questionCount?: number | null
  durationInMinutes?: number | null
  deliveryMethod?: string | null
  lastUpdatedAt?: string | null
}

type Pagination = {
  total: number
  page: number
  lastPage: number
  from: number
  to: number
}

type DepartmentExamStatusResponse = {
  data?: DepartmentExamStatusRow[]
  payload?: {
    pagination?: Pagination
  }
  summary?: DepartmentExamStatusSummary
  semesters?: FilterOption[]
  examTypes?: FilterOption[]
  selectedSemesterId?: number | null
  selectedExamTypeId?: number | null
}

const emptySummary: DepartmentExamStatusSummary = {
  totalCourseCount: 0,
  createdExamCount: 0,
  missingExamCount: 0,
  submittedExamCount: 0,
  plannedExamCount: 0,
}

const badgeClassFor = (row: DepartmentExamStatusRow) => {
  if (!row.isExamCreated) {
    return 'badge-light-danger'
  }

  if (row.status === 'Preparing' || row.statusOrder === 0) {
    return 'badge-light-warning'
  }

  if (row.statusOrder >= 4) {
    return 'badge-light-success'
  }

  return 'badge-light-info'
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const DepartmentExamStatusPage = () => {
  const [rows, setRows] = useState<DepartmentExamStatusRow[]>([])
  const [summary, setSummary] = useState<DepartmentExamStatusSummary>(emptySummary)
  const [semesters, setSemesters] = useState<FilterOption[]>([])
  const [examTypes, setExamTypes] = useState<FilterOption[]>([])
  const [semesterId, setSemesterId] = useState<number | ''>('')
  const [examTypeId, setExamTypeId] = useState<number | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get<DepartmentExamStatusResponse>(
        `${API_URL}/Custom/GetDepartmentExamStatus`,
        {
          params: {
            semesterId: semesterId || undefined,
            examTypeId: examTypeId || undefined,
            page,
            items_per_page: pageSize,
            search: search || undefined,
          },
        }
      )

      const body = response.data
      setRows(body.data ?? [])
      setSummary(body.summary ?? emptySummary)
      setSemesters(body.semesters ?? [])
      setExamTypes(body.examTypes ?? [])
      setPagination(body.payload?.pagination ?? null)

      if (!semesterId && body.selectedSemesterId) {
        setSemesterId(body.selectedSemesterId)
      }

      if (!examTypeId && body.selectedExamTypeId) {
        setExamTypeId(body.selectedExamTypeId)
      }
    } catch (requestError) {
      setRows([])
      setSummary(emptySummary)
      setPagination(null)
      setError('Sınav durum bilgileri alınamadı.')
    } finally {
      setLoading(false)
    }
  }, [examTypeId, page, pageSize, search, semesterId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const totalPages = Math.max(pagination?.lastPage ?? 1, 1)
  const totalItems = pagination?.total ?? rows.length
  const visibleFrom = totalItems === 0 ? 0 : (pagination?.from ?? (page - 1) * pageSize) + 1
  const visibleTo = totalItems === 0 ? 0 : Math.min(pagination?.to ?? page * pageSize, totalItems)

  const summaryCards = useMemo(
    () => [
      {
        label: 'Toplam ders',
        value: summary.totalCourseCount,
        icon: '/media/icons/duotune/files/fil002.svg',
        className: 'bg-light-primary text-primary',
      },
      {
        label: 'Oluşturulmayan',
        value: summary.missingExamCount,
        icon: '/media/icons/duotune/general/gen044.svg',
        className: 'bg-light-danger text-danger',
      },
      {
        label: 'Oluşturulan',
        value: summary.createdExamCount,
        icon: '/media/icons/duotune/general/gen043.svg',
        className: 'bg-light-success text-success',
      },
      {
        label: 'Gönderilen',
        value: summary.submittedExamCount,
        icon: '/media/icons/duotune/arrows/arr085.svg',
        className: 'bg-light-info text-info',
      },
      {
        label: 'Planlanan',
        value: summary.plannedExamCount,
        icon: '/media/icons/duotune/general/gen014.svg',
        className: 'bg-light-warning text-warning',
      },
    ],
    [summary]
  )

  const applySearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  return (
    <>
      <PageTitle>Sınav Durum Takibi</PageTitle>

      <div className='d-flex flex-column gap-6'>
        <div className='card'>
          <div className='card-body py-6'>
            <div className='d-flex flex-column flex-xl-row gap-4 align-items-xl-end'>
              <div className='flex-grow-1'>
                <label className='form-label fw-semibold'>Dönem</label>
                <select
                  className='form-select form-select-solid'
                  value={semesterId}
                  onChange={(event) => {
                    setSemesterId(event.target.value ? Number(event.target.value) : '')
                    setPage(1)
                  }}
                >
                  {semesters.map((semester) => (
                    <option key={semester.id} value={semester.id}>
                      {semester.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='flex-grow-1'>
                <label className='form-label fw-semibold'>Sınav türü</label>
                <select
                  className='form-select form-select-solid'
                  value={examTypeId}
                  onChange={(event) => {
                    setExamTypeId(event.target.value ? Number(event.target.value) : '')
                    setPage(1)
                  }}
                >
                  {examTypes.map((examType) => (
                    <option key={examType.id} value={examType.id}>
                      {examType.name}
                    </option>
                  ))}
                </select>
              </div>

              <form className='flex-grow-1' onSubmit={applySearch}>
                <label className='form-label fw-semibold'>Arama</label>
                <div className='input-group input-group-solid'>
                  <span className='input-group-text'>
                    <KTSVG path='/media/icons/duotune/general/gen021.svg' className='svg-icon-2' />
                  </span>
                  <input
                    className='form-control'
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder='Ders, öğretim elemanı veya program'
                  />
                  <button className='btn btn-primary' type='submit'>
                    Ara
                  </button>
                </div>
              </form>

              <button
                type='button'
                className='btn btn-light-primary'
                onClick={loadData}
                disabled={loading}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-2' />
                Yenile
              </button>
            </div>
          </div>
        </div>

        <div className='row g-4'>
          {summaryCards.map((card) => (
            <div className='col-6 col-xl' key={card.label}>
              <div className={`card h-100 ${card.className}`}>
                <div className='card-body py-5'>
                  <div className='d-flex align-items-center gap-3'>
                    <KTSVG path={card.icon} className='svg-icon-2x' />
                    <div>
                      <div className='fs-2 fw-bold'>{card.value}</div>
                      <div className='fw-semibold'>{card.label}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='card'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title'>
              <div className='d-flex flex-column'>
                <span className='fs-4 fw-bold'>Ders sınav durumları</span>
                <span className='text-muted fs-7'>
                  {visibleFrom}-{visibleTo} / {totalItems}
                </span>
              </div>
            </div>
            <div className='card-toolbar'>
              <select
                className='form-select form-select-sm form-select-solid w-100px'
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value))
                  setPage(1)
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className='card-body pt-0'>
            {error && <div className='alert alert-danger'>{error}</div>}

            <div className='table-responsive'>
              <table className='table table-row-dashed table-hover gy-4 align-middle'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='min-w-120px'>Ders kodu</th>
                    <th className='min-w-240px'>Ders</th>
                    <th className='min-w-180px'>Öğretim elemanı</th>
                    <th className='min-w-180px'>Bölüm / Program</th>
                    <th className='min-w-140px'>Durum</th>
                    <th className='min-w-180px'>Plan bilgisi</th>
                    <th className='min-w-120px'>Soru / Süre</th>
                    <th className='min-w-130px'>Son güncelleme</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className='text-center py-10 text-muted'>
                        Yükleniyor...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className='text-center py-10 text-muted'>
                        Kayıt bulunamadı.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.semesterCourseId}>
                        <td>
                          <span className='fw-bold text-gray-800'>{row.courseCode || '-'}</span>
                          <div className='text-muted fs-8'>{row.examTypeName || '-'}</div>
                        </td>
                        <td>
                          <div className='fw-semibold text-gray-900'>{row.courseName || '-'}</div>
                          <div className='text-muted fs-8'>
                            {row.semesterName || '-'} · {row.studentCount ?? 0} öğrenci
                          </div>
                        </td>
                        <td>{row.lecturerName || '-'}</td>
                        <td>{row.departmentName || '-'}</td>
                        <td>
                          <span className={`badge ${badgeClassFor(row)}`}>
                            {row.statusLabel || '-'}
                          </span>
                          {row.isSubmittedToMeasurementCenter && (
                            <div className='text-muted fs-8 mt-1'>Ölçme merkezine iletildi</div>
                          )}
                        </td>
                        <td>
                          {row.isPlanned ? (
                            <>
                              <span className='fw-semibold'>{row.scheduleSummary || 'Planlandı'}</span>
                              {row.scheduleCount > 1 && (
                                <div className='text-muted fs-8'>{row.scheduleCount} salon</div>
                              )}
                            </>
                          ) : (
                            <span className='text-muted'>Planlanmadı</span>
                          )}
                        </td>
                        <td>
                          <div>{row.questionCount ? `${row.questionCount} soru` : '-'}</div>
                          <div className='text-muted fs-8'>
                            {row.durationInMinutes ? `${row.durationInMinutes} dk` : '-'}
                          </div>
                        </td>
                        <td>{formatDateTime(row.lastUpdatedAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 pt-4'>
              <div className='text-muted fs-7'>
                Sayfa {page} / {totalPages}
              </div>
              <div className='d-flex gap-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                >
                  Önceki
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DepartmentExamStatusPage
